import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { verticalFadeIn } from '@helpers/variants';
import MotionTrack from '@stories/Components/Carousel/MotionTrack/MotionTrack';
import ImageZoomModalStyles from '@stories/Components/Media/ImageZoomModal/ImageZoomModal.styles';
import { ButtonStyles } from '@stories/Elements/Forms/Buttons/Button.styles';
import { m } from 'framer-motion';
import styled from 'styled-components';

const Container = styled(m.div).attrs({
  initial: 'hidden',
  animate: 'visible',
  exit: 'hidden',
  variants: verticalFadeIn(0),
})`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100dvh;
  z-index: 10000;
  display: grid;
  place-items: center;
  background-color: rgba(0 0 0 / 70%);
  backdrop-filter: blur(2px);

  @media ${from(Device.MobileLarge)} {
    padding: 32px 24px;
  }

  @media ${from(Device.Tablet)} {
    padding: 32px 48px;
  }
`;

const Content = styled(m.div).attrs({
  initial: 'hidden',
  exit: 'hidden',
  variants: verticalFadeIn(30),
})`
  background-color: ${brand.white};
  width: 100%;
  max-height: 100%;
  position: relative;
  padding-top: ${fluid(20, 32)};
  border-radius: 4px 0px 4px 4px;
  max-width: 800px;
  display: flex;
  flex-direction: column;
  overflow: auto;
  -webkit-overflow-scrolling: touch;

  @media ${from(Device.TabletLarge)} {
    overflow: hidden;
    max-width: 1200px;
    padding: 0px;
  }
`;

const CrossHolder = styled.button.attrs({ type: 'button' })`
  position: absolute;
  border: none;
  background: transparent;
  padding: 0;
  right: 30px;
  top: 30px;

  svg {
    width: 25px;
    height: 25px;
  }

  @media (${until(Device.MobileLarge)}) {
    right: 20px;
    top: 20px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

const Heading = styled.div`
  font-size: 20px;
  ${fonts.helvetica['93-black-extended']}
  text-transform: uppercase;
  margin-bottom: 16px;
  color: ${brand.black};
  padding-right: 48px;

  @media (${until(Device.MobileLarge)}) {
    font-size: 16px;
  }

  @media (${from(Device.Tablet)}) {
    padding-right: 40px;
  }
`;

const Description = styled.div`
  ${fonts.helvetica['73-bold-extended']};
  line-height: 20px;
  margin-bottom: 16px;

  @media (${until(Device.MobileLarge)}) {
    font-size: 12px;
  }
`;

const AgilityId = styled(Description)`
  color: ${brand.grey[70]};
`;

const HeadingSection = styled.section`
  background-color: ${brand.white};
  display: grid;
  padding: 40px 16px 16px;

  @media ${until(Device.TabletLarge)} {
    padding: 0px 50px 16px 16px;
  }

  @media (${from(Device.Tablet)}) {
    gap: 16px;
    padding-left: 30px;
    padding-right: 30px;
  }
`;

const ContentSection = styled.section`
  gap: 8px;
  padding: 0 16px 16px;

  @media (${from(Device.Tablet)}) {
    overflow: unset;
    display: grid;
    grid-template-columns: repeat(2, 50%);
    gap: 16px;
    padding-left: 30px;
    padding-right: 30px;
  }
`;

const ProductSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  border-top: 2px solid ${brand.grey[70]};
  grid-row: span 2;

  @media (${from(Device.Tablet)}) {
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    max-height: 50dvh;
  }
`;

const FeatureSection = styled.div`
  padding: 16px;
`;

const FeatureTitle = styled(Heading)`
  color: ${brand.black};
  margin-bottom: 16px;
  padding-right: 0;
`;

const FeatureList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Feature = styled.li`
  padding-left: 20px;
  position: relative;

  ${fonts.helvetica['55-roman']};
  font-size: 16px;
  line-height: 20px;

  &:before {
    content: '';
    display: block;
    position: absolute;
    left: 0;
    top: 4px;
    background: ${brand.red.base};
    width: 10px;
    height: 10px;
  }
`;

const SystemLogo = styled.img`
  margin-right: auto;
  height: 24px;
`;

const ProductInfo = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 165px;
  padding-bottom: 20px;
  width: 100%;
  position: relative;
  margin: auto;

  @media (${until(Device.MobileLarge)}) {
    ${ButtonStyles.LinkElement} {
      font-size: 10px;
    }
  }
`;

const ProductImage = styled.img`
  margin-top: auto;
`;

const ButtonSection = styled.div`
  grid-column: 2;
  background: ${brand.white};
  padding-top: 10px;
  position: sticky;
  inset: 0;

  @media ${until(Device.TabletLarge)} {
    padding-bottom: 5px;
  }

  a {
    margin-top: 12px;
    width: 100%;
  }
`;

const ProductImages = styled.div`
  margin-bottom: 16px;
  --visibleItems: 3;
  --itemGutter: 8px;
  grid-gap: var(--itemGutter);
  display: flex;
  align-items: center;
  justify-content: center;

  @media ${from(Device.TabletLarge)} {
    --visibleItems: 4;
  }

  > button[data-size] {
    width: 32px;
    background: none;
  }
`;

const Track = styled(MotionTrack)`
  overflow: hidden;
  margin: 0 calc((var(--itemGutter) / 2) * -1);
  width: calc(var(--visibleItems) * (var(--itemGutter) + 64px));
`;

const ProductImageWrapper = styled.li`
  flex: 0 0 calc(100% / var(--visibleItems));
  padding: 0 calc(var(--itemGutter) / 2);

  img {
    position: relative;
    cursor: pointer;
    border: 2px solid ${brand.grey[70]};
    padding-top: 2px;
    object-fit: contain;
    aspect-ratio: 1;
    height: unset;
  }

  &[data-active='true'] {
    img {
      border-top: 4px solid ${brand.red.base};
      padding-top: 0px;
    }
  }
`;

const ZoomControlButton = styled(ImageZoomModalStyles.ZoomControlButton)`
  position: absolute;
  right: 20px;
  bottom: 20px;

  @media ${from(Device.TabletLarge)} {
    width: 40px;
    height: 40px;
  }
`;

export const InfoModalNPIStyles = {
  Container,
  Content,
  CrossHolder,
  Heading,
  Description,
  AgilityId,
  HeadingSection,
  ContentSection,
  SystemLogo,
  ProductInfo,
  ProductImage,
  ProductSection,
  DetailsSection,
  FeatureList,
  FeatureSection,
  FeatureTitle,
  Feature,
  ButtonSection,
  ProductImages,
  Track,
  ProductImageWrapper,
  ZoomControlButton,
};
